/**
 * Design vocabulary for the A4 guest poster: colour modes, font choices and
 * the stock "scan me" copy. Kept out of the component so the options can be
 * unit-tested and reused by any future poster surface.
 */

// ── Colour modes ──────────────────────────────────────────────────────────────

export type PosterMode = 'light' | 'dark';

/**
 * Claymorphism on canvas: every raised element is a rounded surface with an
 * outer drop shadow, a light inner rim along the top and a darker inner rim
 * along the bottom. These tokens mirror `--shadow-clay` from tokens.css.
 *
 * Strictly red + black + white, per the brand palette.
 */
export interface PosterPalette {
  /** Page background, top → bottom. */
  bgFrom: string;
  bgTo: string;
  /** Raised clay surface fill. */
  surface: string;
  /** Slightly recessed surface, used for the URL field. */
  surfaceSunken: string;
  /** Inner rim along the top edge of a clay surface (the "inflated" read). */
  surfaceHighlight: string;
  /** Inner rim along the bottom edge. */
  surfaceShade: string;
  /** Outer drop shadow beneath clay surfaces. */
  shadow: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  /** Text sitting on a light clay chip, whatever the page mode. */
  textOnChip: string;
  accent: string;
  /**
   * The logo chip stays light in both modes: the Pixel Machha mark is a black
   * camera body, which disappears on a dark surface.
   */
  logoChip: string;
  /** QR card is always light — inverted QR codes fail on many scanners. */
  qrCardBg: string;
  qrFg: string;
  /** Decorative background blobs. */
  blob: string;
  /** Ink for background pattern strokes/fills. */
  pattern: string;
  /** Fill for neutral clay ornaments. */
  ornament: string;
  divider: string;
}

export const POSTER_PALETTES: Record<PosterMode, PosterPalette> = {
  light: {
    bgFrom: '#fffdfd',
    bgTo: '#f4e8e8',
    surface: '#ffffff',
    surfaceSunken: '#faf1f1',
    surfaceHighlight: 'rgba(255, 255, 255, 0.95)',
    surfaceShade: 'rgba(20, 7, 8, 0.07)',
    shadow: 'rgba(76, 18, 22, 0.28)',
    textPrimary: '#140708',
    textSecondary: '#5b5052',
    textMuted: '#9a8f91',
    textOnChip: '#140708',
    accent: '#ef4444',
    logoChip: '#ffffff',
    qrCardBg: '#ffffff',
    qrFg: '#140708',
    blob: 'rgba(239, 68, 68, 0.10)',
    pattern: 'rgba(239, 68, 68, 0.13)',
    ornament: '#fbdcdc',
    divider: 'rgba(20, 7, 8, 0.10)',
  },
  dark: {
    bgFrom: '#1d1416',
    bgTo: '#0a090b',
    surface: '#271d20',
    surfaceSunken: '#1a1315',
    surfaceHighlight: 'rgba(255, 255, 255, 0.10)',
    surfaceShade: 'rgba(0, 0, 0, 0.40)',
    shadow: 'rgba(0, 0, 0, 0.62)',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.74)',
    textMuted: 'rgba(255, 255, 255, 0.45)',
    textOnChip: '#140708',
    accent: '#ef4444',
    logoChip: '#f7f1f1',
    qrCardBg: '#ffffff',
    qrFg: '#140708',
    blob: 'rgba(239, 68, 68, 0.16)',
    pattern: 'rgba(239, 68, 68, 0.20)',
    ornament: '#3a2124',
    divider: 'rgba(255, 255, 255, 0.14)',
  },
};

// ── Background patterns ───────────────────────────────────────────────────────

export type PosterBackground =
  | 'glow'
  | 'plain'
  | 'dots'
  | 'grid'
  | 'diagonal'
  | 'confetti'
  | 'rings'
  | 'waves'
  | 'bokeh'
  | 'mesh'
  | 'topography'
  | 'arcs'
  | 'triangles'
  | 'crosshatch'
  | 'terrazzo'
  | 'halftone'
  | 'chevron'
  | 'scatter';

/**
 * Knobs a pattern can expose. The modal renders a slider for each knob the
 * selected pattern declares, so adding a pattern needs no UI change.
 *
 * Every knob is a multiplier around 1 rather than an absolute value, which
 * keeps the sliders meaningful across patterns with very different geometry.
 */
export type PatternKnob = 'scale' | 'thickness' | 'density' | 'opacity' | 'angle';

export interface PatternKnobMeta {
  label: string;
  min: number;
  max: number;
  step: number;
  /** Rendered next to the value, e.g. "°". */
  unit?: string;
}

export const PATTERN_KNOBS: Record<PatternKnob, PatternKnobMeta> = {
  scale: { label: 'Spacing', min: 0.4, max: 2.5, step: 0.05 },
  thickness: { label: 'Thickness', min: 0.3, max: 4, step: 0.05 },
  density: { label: 'Quantity', min: 0.2, max: 3, step: 0.05 },
  opacity: { label: 'Strength', min: 0.1, max: 2, step: 0.05 },
  angle: { label: 'Angle', min: 0, max: 180, step: 5, unit: '°' },
};

export type PatternSettings = Record<PatternKnob, number>;

export interface PosterBackgroundDef {
  key: PosterBackground;
  label: string;
  knobs: PatternKnob[];
  /** Starting values for this pattern's knobs; anything omitted starts at 1. */
  defaults?: Partial<PatternSettings>;
}

export const POSTER_BACKGROUNDS: PosterBackgroundDef[] = [
  { key: 'plain', label: 'Plain gradient', knobs: [] },
  { key: 'glow', label: 'Soft glow', knobs: ['scale', 'opacity'] },
  { key: 'mesh', label: 'Colour mesh', knobs: ['scale', 'opacity'] },
  { key: 'dots', label: 'Polka dots', knobs: ['scale', 'thickness', 'opacity'] },
  { key: 'halftone', label: 'Halftone fade', knobs: ['scale', 'thickness', 'opacity', 'angle'] },
  { key: 'grid', label: 'Grid lines', knobs: ['scale', 'thickness', 'opacity', 'angle'] },
  { key: 'crosshatch', label: 'Crosshatch', knobs: ['scale', 'thickness', 'opacity', 'angle'] },
  { key: 'diagonal', label: 'Diagonal stripes', knobs: ['scale', 'thickness', 'opacity', 'angle'] },
  { key: 'chevron', label: 'Chevron', knobs: ['scale', 'thickness', 'opacity'] },
  { key: 'waves', label: 'Waves', knobs: ['scale', 'thickness', 'opacity'] },
  { key: 'topography', label: 'Topographic', knobs: ['scale', 'thickness', 'opacity'] },
  { key: 'arcs', label: 'Arcs', knobs: ['scale', 'thickness', 'opacity', 'angle'] },
  { key: 'rings', label: 'Concentric rings', knobs: ['scale', 'thickness', 'opacity'] },
  { key: 'confetti', label: 'Confetti', knobs: ['density', 'thickness', 'opacity'] },
  { key: 'terrazzo', label: 'Terrazzo', knobs: ['density', 'thickness', 'opacity'] },
  { key: 'triangles', label: 'Triangles', knobs: ['density', 'thickness', 'opacity'] },
  { key: 'scatter', label: 'Scattered pills', knobs: ['density', 'thickness', 'opacity', 'angle'] },
  { key: 'bokeh', label: 'Bokeh circles', knobs: ['density', 'thickness', 'opacity'] },
];

export const DEFAULT_POSTER_BACKGROUND: PosterBackground = 'glow';

export function getBackgroundDef(key: PosterBackground): PosterBackgroundDef {
  return POSTER_BACKGROUNDS.find((b) => b.key === key) ?? POSTER_BACKGROUNDS[0];
}

/** Knob values for a pattern, with its own defaults applied. */
export function defaultPatternSettings(key: PosterBackground): PatternSettings {
  const def = getBackgroundDef(key);
  return {
    scale: 1,
    thickness: 1,
    density: 1,
    opacity: 1,
    angle: 45,
    ...def.defaults,
  };
}

// ── Background colours ────────────────────────────────────────────────────────

/**
 * Curated primary/secondary pairs. The poster's ink is derived from the
 * chosen colours' luminance (see `resolvePalette`), so a dark pair
 * automatically flips the whole poster to light type.
 */
export interface BackgroundPreset {
  key: string;
  label: string;
  primary: string;
  secondary: string;
}

export const BACKGROUND_PRESETS: BackgroundPreset[] = [
  { key: 'blush', label: 'Blush (light)', primary: '#fffdfd', secondary: '#f4e8e8' },
  { key: 'paper', label: 'Paper white', primary: '#ffffff', secondary: '#f2f2f4' },
  { key: 'ember', label: 'Ember (dark)', primary: '#1d1416', secondary: '#0a090b' },
  { key: 'ink', label: 'Ink black', primary: '#151517', secondary: '#000000' },
  { key: 'crimson', label: 'Crimson', primary: '#7f1d1d', secondary: '#2a0a0a' },
  { key: 'signal', label: 'Signal red', primary: '#ef4444', secondary: '#991b1b' },
  { key: 'bone', label: 'Bone', primary: '#faf7f2', secondary: '#e8ded2' },
  { key: 'charcoal', label: 'Charcoal', primary: '#2c2c30', secondary: '#111113' },
];

export const DEFAULT_BG_PRIMARY = BACKGROUND_PRESETS[0].primary;
export const DEFAULT_BG_SECONDARY = BACKGROUND_PRESETS[0].secondary;

// ── Clay ornaments ────────────────────────────────────────────────────────────

export type PosterOrnaments =
  | 'mixed'
  | 'orbs'
  | 'sparkles'
  | 'rings'
  | 'pills'
  | 'squares'
  | 'triangles'
  | 'diamonds'
  | 'none';

export const POSTER_ORNAMENTS: { key: PosterOrnaments; label: string }[] = [
  { key: 'mixed', label: 'Mixed shapes' },
  { key: 'orbs', label: 'Clay orbs' },
  { key: 'sparkles', label: 'Sparkles' },
  { key: 'rings', label: 'Clay rings' },
  { key: 'pills', label: 'Pills' },
  { key: 'squares', label: 'Rounded squares' },
  { key: 'triangles', label: 'Triangles' },
  { key: 'diamonds', label: 'Diamonds' },
  { key: 'none', label: 'None' },
];

export const DEFAULT_POSTER_ORNAMENTS: PosterOrnaments = 'mixed';

export const ORNAMENT_COUNT = { min: 0, max: 24, step: 1, default: 7 };

// ── Mascots ───────────────────────────────────────────────────────────────────

export type PosterMascots = 'both' | 'photographer' | 'welcome' | 'none';

export const POSTER_MASCOTS: { key: PosterMascots; label: string }[] = [
  { key: 'both', label: 'Both mascots' },
  { key: 'photographer', label: 'Photographer only' },
  { key: 'welcome', label: 'Welcome mascot only' },
  { key: 'none', label: 'No mascot' },
];

export const DEFAULT_POSTER_MASCOTS: PosterMascots = 'both';

export type PosterMascotPosition = 'split' | 'left' | 'right' | 'center';

export const POSTER_MASCOT_POSITIONS: { key: PosterMascotPosition; label: string }[] = [
  { key: 'split', label: 'Spread to both corners' },
  { key: 'left', label: 'Bottom left' },
  { key: 'right', label: 'Bottom right' },
  { key: 'center', label: 'Bottom centre' },
];

export const DEFAULT_MASCOT_POSITION: PosterMascotPosition = 'split';

export type PosterMascotSize = 'sm' | 'md' | 'lg' | 'xl';

/** Multiplier on the mascot band height. */
export const MASCOT_SIZES: { key: PosterMascotSize; label: string; scale: number }[] = [
  { key: 'sm', label: 'Small', scale: 0.68 },
  { key: 'md', label: 'Medium', scale: 0.85 },
  { key: 'lg', label: 'Large', scale: 1 },
  { key: 'xl', label: 'Extra large', scale: 1.2 },
];

export const DEFAULT_MASCOT_SIZE: PosterMascotSize = 'lg';

export function getMascotScale(size: PosterMascotSize): number {
  return MASCOT_SIZES.find((s) => s.key === size)?.scale ?? 1;
}

// ── Deriving a palette from the chosen background colours ────────────────────

/** WCAG relative luminance, used to decide whether the page needs light ink. */
export function relativeLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 1;
  const int = parseInt(m[1], 16);
  const srgb = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

/**
 * Build the palette actually used for drawing.
 *
 * The ink set is chosen from the background's own luminance rather than from
 * the light/dark toggle, so a custom colour pair can never produce a poster
 * with unreadable type. The toggle instead seeds the default colours.
 */
export function resolvePalette(primary: string, secondary: string): PosterPalette {
  const lum = (relativeLuminance(primary) + relativeLuminance(secondary)) / 2;
  const base = POSTER_PALETTES[lum > 0.42 ? 'light' : 'dark'];
  return { ...base, bgFrom: primary, bgTo: secondary };
}

// ── Fonts ─────────────────────────────────────────────────────────────────────

export interface PosterFont {
  key: string;
  label: string;
  /** CSS font-family stack passed to the canvas 2D context. */
  stack: string;
  /**
   * Google Fonts family spec (`Family+Name:wght@...`). Absent for fonts that
   * ship with the OS, which need no network fetch.
   */
  google?: string;
  /** Rough cap-height ratio, used to keep optical sizing even across faces. */
  scale?: number;
}

export const POSTER_FONTS: PosterFont[] = [
  {
    key: 'inter',
    label: 'Inter',
    stack: '"Inter", system-ui, sans-serif',
    google: 'Inter:wght@400;500;600;800',
  },
  {
    key: 'poppins',
    label: 'Poppins',
    stack: '"Poppins", "Inter", system-ui, sans-serif',
    google: 'Poppins:wght@400;500;600;800',
    scale: 0.96,
  },
  {
    key: 'playfair',
    label: 'Playfair Display',
    stack: '"Playfair Display", Georgia, serif',
    google: 'Playfair+Display:wght@500;700;900',
    scale: 1.02,
  },
  {
    key: 'bebas',
    label: 'Bebas Neue',
    stack: '"Bebas Neue", Impact, sans-serif',
    google: 'Bebas+Neue',
    scale: 1.22,
  },
  {
    key: 'lora',
    label: 'Lora',
    stack: '"Lora", Georgia, serif',
    google: 'Lora:wght@400;500;700',
  },
  {
    key: 'caveat',
    label: 'Caveat (handwritten)',
    stack: '"Caveat", "Bradley Hand", cursive',
    google: 'Caveat:wght@500;700',
    scale: 1.3,
  },
  { key: 'georgia', label: 'Georgia (system)', stack: 'Georgia, "Times New Roman", serif' },
  {
    key: 'system',
    label: 'System sans (system)',
    stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  },
  {
    key: 'mono',
    label: 'Monospace (system)',
    stack: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    scale: 0.94,
  },
];

export const DEFAULT_FONT_KEY = 'inter';

export function getPosterFont(key: string): PosterFont {
  return POSTER_FONTS.find((f) => f.key === key) ?? POSTER_FONTS[0];
}

/**
 * Which parts of the poster carry an independent font choice.
 * The invite URL is intentionally not configurable — it stays monospace so
 * guests can transcribe it character by character without ambiguity.
 */
export interface PosterFontChoices {
  eventName: string;
  message: string;
  albumName: string;
}

export const DEFAULT_FONT_CHOICES: PosterFontChoices = {
  eventName: DEFAULT_FONT_KEY,
  message: DEFAULT_FONT_KEY,
  albumName: DEFAULT_FONT_KEY,
};

export const FONT_SLOTS: { key: keyof PosterFontChoices; label: string }[] = [
  { key: 'eventName', label: 'Event name' },
  { key: 'message', label: 'Message' },
  { key: 'albumName', label: 'Album name' },
];

// ── Google Fonts loading ──────────────────────────────────────────────────────

/** Keyed by font key; the promise resolves once its stylesheet has parsed. */
const stylesheets = new Map<string, Promise<void>>();

/**
 * Add a font's Google Fonts stylesheet, once, and resolve when the browser has
 * parsed it. Resolving on error too — a missing web font degrades to the
 * stack's fallback rather than blocking the poster.
 */
function injectStylesheet(font: PosterFont): Promise<void> {
  const existing = stylesheets.get(font.key);
  if (existing) return existing;

  const promise = new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
  stylesheets.set(font.key, promise);
  return promise;
}

/**
 * Inject the stylesheets for any web fonts in use and wait until their faces
 * are actually available. Canvas silently substitutes a fallback if you draw
 * before the face loads, so the await is what makes the poster correct.
 *
 * The two phases must stay ordered: `document.fonts.load()` matches against
 * faces already in the CSSOM, so calling it before the stylesheet parses
 * resolves against nothing and reports success without loading anything.
 */
export async function ensurePosterFontsLoaded(keys: string[]): Promise<void> {
  if (typeof document === 'undefined') return;

  const fonts = keys.map(getPosterFont).filter((f) => f.google);
  if (fonts.length === 0) return;

  await Promise.all(fonts.map(injectStylesheet));

  if (!document.fonts) return;
  await Promise.all(
    fonts.flatMap((font) => {
      // Family name is the first quoted entry of the stack.
      const family = font.stack.match(/^"([^"]+)"/)?.[1];
      if (!family) return [];
      // Every weight the poster can draw with. Families that ship fewer (Bebas
      // Neue has only 400) resolve to their nearest face.
      return [400, 500, 700, 800].map((w) =>
        document.fonts.load(`${w} 64px "${family}"`).catch(() => undefined),
      );
    }),
  ).catch(() => undefined);
}

// ── Message presets ───────────────────────────────────────────────────────────

export const POSTER_MESSAGES: string[] = [
  'Scan to get your event photos',
  'Scan the code to see your photos',
  'Your photos are here — just scan',
  'Scan for instant access to event photos',
  'Point your camera here for the photo album',
  'Find yourself in the photos — scan to start',
  'All of today’s photos, one scan away',
  'Scan to browse and download your photos',
  'Relive the moments — scan to view photos',
  'Snap, scan, and save your memories',
  'Scan to view the live photo album',
  'Photos from the event — scan to open',
  'Scan me for your event photos',
  'Get your pictures — scan the QR code',
];

export const DEFAULT_POSTER_MESSAGE = POSTER_MESSAGES[0];
